// oxlint-disable typescript/no-explicit-any
/* eslint-disable @stylistic/js/max-len */

// Helper type for creating descriptive error messages for resolver validation
type ResolverValidationError<Message extends string> = { 'RESOLVER_VALIDATION_ERROR:': Message };

// Helper to create a tuple of 'unknown's with the same length as T
// Fills the rest of the output tuple positions with unknown. This signals that an error has occurred,
// and the types of subsequent resolvers in this validation path are considered unknown.
type MapToUnknown<T extends readonly unknown[]> = { -readonly [K in keyof T]: unknown };

// Recursive helper to validate the structure of the resolvers tuple.
// TInput: The expected input type for the current resolver in the chain.
// TChain: The remaining part of the resolvers tuple to validate.
type ValidatedResolvers<TInput, TChain extends readonly unknown[]> = TChain extends readonly []
  ? readonly [] // Base case: empty chain segment is valid
  : TChain extends readonly [infer Head, ...infer Tail]
    ? Head extends (input: TInput) => infer OutputP
      ? // Valid: Head is a function and its input parameter is compatible with TInput.
        // Recurse for the tail.
        readonly [Head, ...ValidatedResolvers<Awaited<OutputP>, Tail>]
      : // Error: Head is not a function or its signature is incompatible with TInput.
        readonly [
          ResolverValidationError<'Invalid resolver: Function signature is incompatible. Ensure it accepts the output of the previous resolver.'>,
          ...MapToUnknown<Tail>, // Mark rest of the chain as problematic.
        ]
    : // Should be unreachable if TChain is always a tuple from spread operator
      readonly [ResolverValidationError<'Internal Error: Invalid chain structure'>];

// Recursive helper to compute the final result type of the promise chain.
// TInput: The current accumulated type in the chain.
// TChain: The remaining part of the resolvers tuple.
type ComputeChainResult<TInput, TChain extends readonly unknown[]> = TChain extends readonly [infer Head, ...infer Tail]
  ? // Case 1: Head is a function that accepts TInput (this is the only valid function type now)
    Head extends (input: TInput) => infer OutputP
    ? ComputeChainResult<Awaited<OutputP>, Tail>
    : never // Should not be reached if TChain is correctly validated by ValidatedResolvers
  : TInput; // Base case: the final accumulated result type

// Overload 0: No resolvers passed
export function asyncResolver(): <T>(value: T) => Promise<T>;

// Overload 1: One resolver
export function asyncResolver<TInitial, R1>(res1: (input: TInitial) => R1): (value: TInitial) => Promise<Awaited<R1>>;

// Overload 2: Two resolvers
export function asyncResolver<TInitial, R1, R2>(
  res1: (input: TInitial) => R1,
  res2: (input: Awaited<R1>) => R2,
): (value: TInitial) => Promise<Awaited<R2>>;

// Overload 3: Three resolvers
export function asyncResolver<TInitial, R1, R2, R3>(
  res1: (input: TInitial) => R1,
  res2: (input: Awaited<R1>) => R2,
  res3: (input: Awaited<R2>) => R3,
): (value: TInitial) => Promise<Awaited<R3>>;

// Overload 4: Four resolvers
export function asyncResolver<TInitial, R1, R2, R3, R4>(
  res1: (input: TInitial) => R1,
  res2: (input: Awaited<R1>) => R2,
  res3: (input: Awaited<R2>) => R3,
  res4: (input: Awaited<R3>) => R4,
): (value: TInitial) => Promise<Awaited<R4>>;

// Overload 5: Five resolvers
export function asyncResolver<TInitial, R1, R2, R3, R4, R5>(
  res1: (input: TInitial) => R1,
  res2: (input: Awaited<R1>) => R2,
  res3: (input: Awaited<R2>) => R3,
  res4: (input: Awaited<R3>) => R4,
  res5: (input: Awaited<R4>) => R5,
): (value: TInitial) => Promise<Awaited<R5>>;

// Original variadic overload (acts as a fallback)
// TResolvers must be a non-empty tuple of functions.
export function asyncResolver<
  const TResolvers extends readonly [(input: any) => any, ...((input: any) => any)[]],
  TInitialValue = TResolvers extends readonly [(input: infer P) => any, ...unknown[]] ? P : unknown,
>(
  // Validate TResolvers structure using the inferred TInitialValue.
  // All resolvers must be functions.
  ...resolvers: TResolvers & ValidatedResolvers<TInitialValue, TResolvers>
): (value: TInitialValue) => Promise<ComputeChainResult<TInitialValue, TResolvers>>;

// Implementation signature - needs to be compatible with both overloads
export function asyncResolver(
  ...resolvers: any[] // General type for implementation parameters
): (value: any) => Promise<any> {
  // General return type for implementation
  if (resolvers.length === 0) {
    // Handle the case for the first overload: asyncResolver()
    // The returned function is generic.
    return <T>(value: T): Promise<T> => Promise.resolve(value);
  }

  // Handle the case for the rest of the overloads: asyncResolver(resolver1, resolver2, ...)
  const actualResolvers = resolvers as readonly ((input: any) => any)[];

  return function resolve(initialValue: any): Promise<any> {
    let currentPromise: Promise<any> = Promise.resolve(initialValue);

    for (const currentStepResolver of actualResolvers) {
      currentPromise = currentPromise.then(currentStepResolver);
    }
    return currentPromise.catch((error) => {
      if (error instanceof AsyncResolverBail) {
        return error.toValue();
      }
      throw error;
    });
  };
}

class AsyncResolverBail extends Error {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax
  constructor(private value: unknown) {
    super();
    this.name = 'AsyncResolverBail';
  }

  toValue() {
    return this.value;
  }
}

asyncResolver.bail = <T>(value: T): Promise<T> => Promise.reject(new AsyncResolverBail(value));
